import { dx, dy, type Direction, type Turn } from '../../sim/direction';
import type { SimLevel } from '../../sim/level';
import type { MowerSpec } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';
import { motionModelFor, type Action, type MotionModel } from './motionModel';
import { NARROW_BODY, StateSpace } from './stateSpace';

/** A straight pass: start at (x, y) facing `dir`, then drive `length` tiles on. */
export interface ArtLeg {
  x: number;
  y: number;
  dir: Direction;
  length: number;
}

export interface ArtResult {
  won: boolean;
  /** Picture match at the end (0..1). */
  match: number;
  ticks: number;
  crashes: number;
  /** Tile centers reached, with the fuel each step burned (for placing gas cans). */
  route: { x: number; y: number; fuel: number }[];
}

/**
 * Paints a lawn-art picture by following a plan of straight passes, finding its way from one pass
 * to the next (and finally to the judges' table) along a shortest path through the mower's state
 * space, so every mower's turn rules are respected. Drives the art bot and the in-game autopilot
 * on art lawns.
 */
export class ArtPilot {
  private readonly model: MotionModel;
  private readonly space: StateSpace;
  private legIndex = 0;
  /** Traveling to the next pass, driving along one, or heading for the judges' table. */
  private mode: 'travel' | 'straight' | 'goal';
  /** Tiles still to drive on the current pass after this center. */
  private straightLeft = 0;
  private plan: Action[] | null = null;
  /** Picture tiles a finished pass has painted (crossing one the wrong way would spoil it). */
  private readonly painted = new Set<number>();

  constructor(
    private readonly sim: Simulation,
    private readonly legs: readonly ArtLeg[],
  ) {
    this.model = motionModelFor(sim.mowerSpec);
    this.space = new StateSpace(sim.grid, this.model, NARROW_BODY(sim.mowerSpec.cutPower));
    this.mode = legs.length > 0 ? 'travel' : 'goal';
  }

  /** At a tile center: the turn to ask for (null to keep going straight). */
  decide(): Turn | null {
    const m = this.sim.mower;
    if (m.progress !== 0) return null;
    // A finished pass hands over to planning at the same center.
    for (let guard = 0; guard < this.legs.length + 2; guard++) {
      if (this.mode === 'straight') {
        if (this.straightLeft > 0) {
          this.straightLeft--;
          return null; // keep driving along the pass
        }
        this.nextLeg();
      }
      if (this.mode === 'travel') {
        const leg = this.legs[this.legIndex]!;
        // Aim for the pass's second tile, arriving along it (through its first tile), so the
        // pass is entered already heading the right way: no reverse needed.
        const into = { x: leg.x + dx(leg.dir), y: leg.y + dy(leg.dir) };
        if (m.x === into.x && m.y === into.y && m.dir === leg.dir) {
          this.mode = 'straight';
          this.straightLeft = leg.length - 1;
          continue;
        }
        this.plan ??=
          this.pathTo(into.x, into.y, leg.dir, [into, { x: leg.x, y: leg.y }]) ??
          this.pathTo(into.x, into.y, leg.dir);
        if (!this.plan || this.plan.length === 0) {
          this.nextLeg(); // unreachable: skip it
          continue;
        }
        const action = this.plan.shift()!;
        if (this.plan.length === 0) {
          this.plan = null;
          this.mode = 'straight';
          this.straightLeft = leg.length - 1;
        }
        return action === 'straight' ? null : action;
      }
      const goal = this.sim.level.art?.goal;
      if (!goal) return null;
      this.plan ??= this.pathTo(goal.x, goal.y);
      const action = this.plan?.shift();
      if (!this.plan || this.plan.length === 0) this.plan = null;
      return !action || action === 'straight' ? null : action;
    }
    return null;
  }

  private nextLeg(): void {
    const leg = this.legs[this.legIndex];
    if (leg) {
      for (let k = 1; k <= leg.length; k++) {
        this.painted.add((leg.y + dy(leg.dir) * k) * this.sim.grid.width + leg.x + dx(leg.dir) * k);
      }
    }
    this.legIndex++;
    this.mode = this.legIndex < this.legs.length ? 'travel' : 'goal';
    this.plan = null;
  }

  /**
   * Actions from the mower's current state to (x, y) facing `dir` (any heading if omitted). With
   * `through` (a picture lawn), the way never spoils what is painted: it crosses a painted tile
   * only heading the way it is striped (or one of `through`, the way into the next pass); tiles
   * still to paint it may cross (their own pass puts them right).
   */
  private pathTo(
    x: number,
    y: number,
    dir?: Direction,
    through?: readonly { x: number; y: number }[],
  ): Action[] | null {
    const art = this.sim.level.art;
    const w = this.sim.grid.width;
    const avoid =
      through && art && !art.goal
        ? (px: number, py: number, heading: Direction) => {
            const i = py * w + px;
            const want = art.pattern[i] ?? 0;
            if (want === 0 || !this.painted.has(i)) return false;
            if (want === (heading % 2 === 1 ? 1 : 2)) return false;
            return !through.some((t) => t.x === px && t.y === py);
          }
        : null;
    const { space, model } = this;
    const m = this.sim.mower;
    const start = space.index(m.x, m.y, m.dir, model.fromMower(m));
    const prev = new Map<number, { from: number; action: Action }>();
    const seen = new Set<number>([start]);
    const queue = [start];
    for (let head = 0; head < queue.length; head++) {
      const i = queue[head]!;
      const s = space.decode(i);
      if (i !== start && s.x === x && s.y === y && (dir === undefined || s.dir === dir)) {
        const actions: Action[] = [];
        for (let k = i; k !== start; k = prev.get(k)!.from) actions.unshift(prev.get(k)!.action);
        return actions;
      }
      space.forEachSuccessor(i, (j, action) => {
        if (seen.has(j)) return;
        if (avoid) {
          const t = space.decode(j);
          if (avoid(t.x, t.y, t.dir)) return;
        }
        seen.add(j);
        prev.set(j, { from: i, action });
        queue.push(j);
      });
    }
    return null;
  }
}

/** Paints a lawn-art picture with the {@link ArtPilot}: can it be done, and how well? */
export function solveArt(
  level: SimLevel,
  mower: MowerSpec,
  legs: readonly ArtLeg[],
  { maxTicks = 200_000, ignoreFuel = false }: { maxTicks?: number; ignoreFuel?: boolean } = {},
): ArtResult {
  // An "endless" tank is a huge one, so the fuel each step burns can still be measured.
  const spec: MowerSpec =
    ignoreFuel && mower.fuelTiles !== undefined ? { ...mower, fuelTiles: 1_000_000_000 } : mower;
  const sim = new Simulation(level, spec);
  sim.drainEvents();
  const pilot = new ArtPilot(sim, legs);
  let crashes = 0;
  const route = [{ x: sim.mower.x, y: sim.mower.y, fuel: 0 }];
  let fuel = sim.mower.fuel;
  while (sim.status === 'running' && sim.tick < maxTicks) {
    const turn = pilot.decide();
    if (turn) sim.enqueue({ type: 'turn', turn });
    for (const e of sim.step()) if (e.type === 'crashed') crashes++;
    const now = sim.mower;
    const last = route[route.length - 1];
    if (now.progress === 0 && (last?.x !== now.x || last.y !== now.y)) {
      route.push({
        x: now.x,
        y: now.y,
        fuel: Number.isFinite(now.fuel) && now.fuel < fuel ? fuel - now.fuel : 1,
      });
    }
    fuel = now.fuel;
  }
  return { won: sim.status === 'won', match: sim.artMatch() ?? 0, ticks: sim.tick, crashes, route };
}
